import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useGuideStore } from "../store/guideStore";
import { Button, Card, CardContent } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, BookOpen, X } from "lucide-react";
import guideImage from "../assets/images/guide.png";
import { usePenpal } from "../hooks/usePenpal";
import { usePublishGuide } from "../hooks/usePublishGuide";
import { useNavigateWithLock } from "../hooks/useNavigateWithLock";
import UpsellModal from "../components/modals/UpsellModal";
import PublishingProgress from "../components/features/guide/PublishingProgress";
import { AppLayout } from "../components/layout";
import AnnouncementBanner from "../components/AnnouncementBanner";

const AddGuidePage: React.FC = () => {
  const { t } = useTranslation();
  const { cancelExport } = usePenpal();
  const { guideToPublish, steps } = useGuideStore();
  const navigate = useNavigateWithLock();

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { handlePublish, isPublishing } = usePublishGuide({
    onError: (message) => {
      setErrorMessage(message);
      setShowErrorModal(true);
    },
    onUpgradeNeeded: () => {
      setShowUpsellModal(true);
    },
  });

  const handlePublishClick = () => {
    handlePublish();
  };

  const handleCancel = () => {
    cancelExport();
    navigate('/guides');
  };

  return (
    <AppLayout showSidebar={false} showBillingInfoCard={true}>
      <div className="bg-gray-50 mb-5 px-4 py-8 relative min-h-[550px]">
        {/* Announcement Banner */}
        <AnnouncementBanner />
        {/* Guide Preview Card */}
        {!isPublishing && guideToPublish && (
          <Card className="my-8 pt-2">
            <CardContent>
              <div className="flex items-start gap-6 p-6 rounded-lg">
                <div className="relative">
                  <img
                    src={guideImage}
                    className="w-16 h-16 object-cover"
                    alt={t("addGuide.guideImageAlt")}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold text-foreground mb-2 line-clamp-2">
                    {guideToPublish.title}
                  </h3>

                  <div
                    className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(guideToPublish.description ?? ""),
                    }}
                  />

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <BookOpen className="h-4 w-4" />
                      <span>{t("addGuide.steps", { count: steps.length })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Publishing Progress */}
        <PublishingProgress />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          {!isPublishing && (
            <Button size="lg" onClick={handlePublishClick}>
              {t("addGuide.publishGuide")}
            </Button>
          )}

          <Button variant="outline" size="lg" onClick={handleCancel}>
            <X className="h-5 w-5 mr-2" />
            {t("common.cancel")}
          </Button>
        </div>

        {/* Error Dialog */}
        <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <DialogTitle>{t("addGuide.publishFailedTitle")}</DialogTitle>
              </div>
              <DialogDescription>
                {t("addGuide.publishFailedDescription")}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="text-sm text-destructive font-medium">
                  {errorMessage}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowErrorModal(false)}
                className="w-full sm:w-auto"
              >
                {t("common.close")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upsell Modal */}
        <UpsellModal
          isOpen={showUpsellModal}
          onClose={() => setShowUpsellModal(false)}
        />
      </div>
    </AppLayout>
  );
};

export default AddGuidePage;
